import { ReactNode, useMemo, useState } from 'react'
import { Box, SxProps } from '@mui/material'
import { Form, Formik, useField, useFormikContext } from 'formik'
import { t } from 'i18next'

import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import {
  validationSchema,
  initialValues,
  validationSchemaIntervinienteMonitorio
} from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/Interviniente/types'
import { useSnackbarContext } from 'src/contexts/SnackbarContext'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { useFetchTipoVia } from 'src/hooks/fetch/useCatalogoGeneralFetch'
import { Orden, OrganoJudicial } from 'src/interfaces/Catalog'
import { Interviniente } from 'src/interfaces/EscritosLegales'
import RequiredText from '../../../RequiredText'
import RepresentanteLegalView from '../RepresentanteLegal/RepresentanteLegalView'
import RepresentanteProcesalView from '../RepresentanteProcesal/RepresentanteProcesalView'
import IntervinienteForm from './IntervinienteForm'

import { intervinienteViewStyles as styles } from './IntervinienteViewStyles'

type Props = {
  closeDialog: () => void
  formValues?: Interviniente | null
  title?: string
  children?: ReactNode
}

export default function IntervinienteView({
  closeDialog,
  formValues,
  title = '',
  children
}: Props): JSX.Element {
  const [intervinientes] = useField<Interviniente[]>(FormField.INTERVINIENTES)
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const [organo] = useField<OrganoJudicial>(`${FormField.ORGANO_DESTINO_ORGANO_JUDICIAL_FORM}`)
  const { setFieldValue } = useFormikContext()
  const { setSuccessMessage } = useSnackbarContext()
  const [disabledBtn, setDisabledBtn] = useState(false)
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(false)

  const { tipoMensaje } = useTipoMensajeContext()
  const { data: tipoViaData } = useFetchTipoVia([FormField.INTERVINIENTE_DIRECCION_TIPO_VIA])

  const intervinienteValidationSchema = useMemo(
    () =>
      tipoMensaje === TipoMensaje.MONITORIO
        ? validationSchemaIntervinienteMonitorio(tipoViaData)
        : validationSchema(tipoViaData),
    [tipoMensaje, tipoViaData]
  )

  function handleSubmit(values: Interviniente): void {
    setDisabledBtn(true)

    const index = intervinientes.value.findIndex(
      (item: Interviniente) => JSON.stringify(item) === JSON.stringify(formValues)
    )

    values.validated = true

    let newData: Interviniente[] = []

    if (index >= 0) {
      intervinientes.value[index] = values
      newData = intervinientes.value
      setSuccessMessage(t('alert.register_edited_success'))
    } else {
      newData = [...intervinientes.value, values]
      setSuccessMessage(t('alert.register_added_success'))
    }

    setFieldValue(FormField.INTERVINIENTES, newData)

    closeDialog()
  }

  return (
    <Formik
      initialValues={formValues ?? initialValues}
      form="formInterviniente"
      onSubmit={handleSubmit}
      validationSchema={intervinienteValidationSchema}
      validateOnMount={false}
      validateOnChange={validateAfterSubmit}
      validateOnBlur={validateAfterSubmit}
    >
      {({ handleSubmit, submitForm }) => (
        <Form
          onSubmit={event => {
            setValidateAfterSubmit(true)
            handleSubmit(event)
          }}
        >
          <Box sx={styles.wrapperHeader}>
            {children}
            <Box display="flex" flexDirection="row" alignItems="center" sx={styles.header}>
              <Box component="span" flex="1 1 auto" sx={styles.headerTitle}>
                {title}
              </Box>
              <Box flex="0 0 auto">
                <ButtonPrimaryOutlined onClick={closeDialog} variant="text">
                  {t('actions.cancel')}
                </ButtonPrimaryOutlined>
                <ButtonPrimaryOutlined
                  type="submit"
                  onClick={() => submitForm()}
                  disabled={disabledBtn}
                >
                  {t('actions.save')}
                </ButtonPrimaryOutlined>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.form}>
            <RequiredText />
            <IntervinienteForm orden={orden.value} organo={organo.value} />
            <FieldsetGroup legend={t('form.legend.representante')}>
              <RepresentanteLegalView representantesData={formValues?.representantesLegales} />
              <RepresentanteProcesalView
                representantesData={formValues?.representantesProcesales}
                monitorio={tipoMensaje === TipoMensaje.MONITORIO}
                validateAfterSubmit={validateAfterSubmit}
              />
            </FieldsetGroup>
          </Box>
        </Form>
      )}
    </Formik>
  )
}
